import { useState, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { Upload, FileText, CheckCircle, AlertTriangle, Loader2, X } from "lucide-react"
import * as api from "../api/apcm-ops.api"
import type { ChartUploadResult } from "../api/apcm-ops.api"

interface ChartUploadProps {
  orgId: string
  onComplete: () => void
}

export function ChartUpload({ orgId, onComplete }: ChartUploadProps) {
  const [files, setFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<ChartUploadResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFiles = (fileList: FileList | File[]) => {
    const newFiles = Array.from(fileList).filter((f) => f.name.endsWith(".json"))
    if (newFiles.length === 0) {
      setError("Please upload .json files")
      return
    }
    const nonJson = Array.from(fileList).length - newFiles.length
    if (nonJson > 0) {
      setError(`${nonJson} non-JSON file(s) were skipped`)
    } else {
      setError(null)
    }
    setFiles((prev) => [...prev, ...newFiles])
    setResult(null)
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }, [])

  const handleUpload = async () => {
    if (files.length === 0) return
    setShowConfirm(false)
    setIsUploading(true)
    setError(null)
    setResult(null)

    try {
      const res = await api.uploadPatientCharts(orgId, files)
      setResult(res)
      setFiles([])
      onComplete()
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Upload failed")
    } finally {
      setIsUploading(false)
    }
  }

  const clearState = () => {
    setFiles([])
    setResult(null)
    setError(null)
  }

  const totalSize = files.reduce((sum, f) => sum + f.size, 0)

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        className={cn(
          "relative rounded-xl border-2 border-dashed transition-all",
          "flex flex-col items-center justify-center py-8 px-4 text-center",
          isDragging
            ? "border-primary bg-primary/5"
            : files.length > 0
              ? "border-primary/40 bg-primary/[0.02]"
              : "border-border/60 hover:border-primary/30 hover:bg-muted/30",
          isUploading && "pointer-events-none opacity-60",
        )}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => files.length === 0 && inputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".json"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              handleFiles(e.target.files)
            }
            e.target.value = ""
          }}
        />

        {files.length > 0 ? (
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">
                {files.length} JSON file{files.length !== 1 ? "s" : ""} selected
              </p>
              <p className="text-[11px] text-muted-foreground">
                {(totalSize / (1024 * 1024)).toFixed(1)} MB total
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={(e) => { e.stopPropagation(); clearState() }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <>
            <div className="rounded-full bg-muted/60 p-3 mb-3">
              <Upload className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">
              Drop simplified patient chart JSONs here or click to browse
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 max-w-sm">
              Upload Stage 3/4 output from the FHIR pipeline. Files are matched to patients by FHIR ID or MRN.
            </p>
          </>
        )}
      </div>

      {/* Submit button */}
      {files.length > 0 && !result && (
        <div className="flex items-center justify-end">
          <Button
            size="sm"
            className="bg-primary hover:bg-primary-hover text-white text-xs h-9 px-4"
            disabled={isUploading}
            onClick={() => setShowConfirm(true)}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Upload Charts
              </>
            )}
          </Button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="rounded-xl border border-success/30 bg-success/10 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-success" />
            <span className="text-sm font-semibold text-success">Chart Upload Complete</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
            <ResultStat label="Matched" value={result.matched} accent />
            <ResultStat label="New" value={result.created_new} accent />
            <ResultStat label="Updated" value={result.updated_existing} />
            <ResultStat label="Unmatched" value={result.unmatched} />
          </div>
          {result.unmatched_files.length > 0 && (
            <div className="flex items-start gap-2 mt-2 rounded-lg bg-warning/10 border border-warning/30 p-2">
              <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] font-medium text-warning">
                  {result.unmatched_files.length} file(s) could not be matched to patients
                </p>
                {result.unmatched_files.slice(0, 5).map((f, i) => (
                  <p key={i} className="text-[11px] text-warning mt-0.5 font-mono truncate max-w-xs">
                    {f}
                  </p>
                ))}
                {result.unmatched_files.length > 5 && (
                  <p className="text-[11px] text-warning mt-1">
                    ...and {result.unmatched_files.length - 5} more
                  </p>
                )}
              </div>
            </div>
          )}
          {result.errors.length > 0 && (
            <div className="flex items-start gap-2 mt-2 rounded-lg bg-destructive/10 border border-destructive/30 p-2">
              <AlertTriangle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] font-medium text-destructive">
                  {result.errors.length} error(s)
                </p>
                {result.errors.slice(0, 3).map((e, i) => (
                  <p key={i} className="text-[11px] text-destructive mt-0.5 font-mono truncate max-w-xs">
                    {e}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && !result && (
        <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3">
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Patient Charts</DialogTitle>
            <DialogDescription>
              This will upload <strong>{files.length} JSON file{files.length !== 1 ? "s" : ""}</strong>{" "}
              ({(totalSize / (1024 * 1024)).toFixed(1)} MB) and create health summary records for matched patients.
              No care plans will be generated — use "Generate Care Plans" after upload.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary-hover"
              onClick={handleUpload}
            >
              Confirm & Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ResultStat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="text-center">
      <p className={cn(
        "text-xl font-semibold tabular-nums",
        accent ? "text-primary" : "text-foreground"
      )}>
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
    </div>
  )
}
